import { formatInTimeZone } from "date-fns-tz"

import { escapeCsvValue } from "@/utils/csv/csv-formatter"
import { toISODateString } from "@/utils/date/date-range-helpers"
import { BUCKET_INTERVAL } from "@/utils/date/timeBucketing"
import { getUserTimezone } from "@/utils/date/timezone"
import { downloadBlob } from "@/utils/download"

import type { TableRowData } from "../components/TableWidgetTable"
import type { ChartDataPoint, ChartWidgetConfig } from "../types/chart"
import {
  splitKeyToDisplayName,
  type DisplayNameLookups,
} from "./chart/displayNameUtils"
import {
  getMetadataGroupDisplayName,
  hasChartGrouping,
} from "./chart/splitKeys"
import { resolveDataSource } from "./dataSource"
import { formatMetricCellValue } from "./tableWidgetData"

interface WidgetCSVExportOptions {
  widgetTitle: string
  config: ChartWidgetConfig
  chartData: ChartDataPoint[]
  splitKeys: string[]
  displayNameLookups: DisplayNameLookups | null
  dateRange?: {
    start?: Date
    end?: Date
  }
  metricDisplayName?: string
}

export function convertWidgetDataToCSV(
  options: WidgetCSVExportOptions,
): string {
  const {
    widgetTitle,
    config,
    chartData,
    splitKeys,
    displayNameLookups,
    dateRange,
    metricDisplayName,
  } = options

  if (chartData.length === 0) {
    return ""
  }

  const csvRows: string[] = []

  csvRows.push(`# Widget: ${escapeCsvValue(widgetTitle)}`)
  csvRows.push(
    `# Metric: ${escapeCsvValue(metricDisplayName || config.metricId || "Unknown")}`,
  )
  csvRows.push(`# Aggregation: ${escapeCsvValue(config.aggregation)}`)
  csvRows.push(`# Data Source: ${escapeCsvValue(resolveDataSource(config))}`)
  csvRows.push(
    `# Bucket Interval: ${escapeCsvValue(config.bucketInterval || BUCKET_INTERVAL.FOUR_HOURS)}`,
  )

  if (dateRange?.start && dateRange?.end) {
    csvRows.push(
      `# Date Range: ${dateRange.start.toISOString()} to ${dateRange.end.toISOString()}`,
    )
  }

  const timezone = getUserTimezone()
  csvRows.push(`# Timezone: ${escapeCsvValue(timezone)}`)

  if (hasChartGrouping(config)) {
    csvRows.push(
      `# Split By: ${escapeCsvValue(config.groupByMetadataKey || config.groupBy || config.splitBy || "")}`,
    )
  }

  if (
    config.filters?.agentIds?.length ||
    config.filters?.personaIds?.length ||
    config.filters?.templateNames?.length ||
    config.filters?.tagFilters?.length ||
    config.filters?.metadata?.length
  ) {
    csvRows.push(`# Filters Applied: Yes`)
  }

  csvRows.push("")

  const hasSplitKeys =
    splitKeys.length > 0 &&
    (hasChartGrouping(config) || config.metricOutputType === "string")

  if (hasSplitKeys) {
    return convertMultiSeriesToCSV(
      csvRows,
      chartData,
      splitKeys,
      config,
      displayNameLookups,
      timezone,
    )
  }

  return convertSingleSeriesToCSV(csvRows, chartData, timezone)
}

function formatCSVTime(point: ChartDataPoint, timezone: string): string {
  // Time buckets have ISO starts; totals and histogram bins use non-date labels.
  if (!/^\d{4}-\d{2}-\d{2}T/.test(point.bucketTime)) {
    return point.date
  }

  const bucketStart = new Date(point.bucketTime)
  if (Number.isNaN(bucketStart.getTime())) {
    return point.date
  }

  return formatInTimeZone(bucketStart, timezone, "yyyy-MM-dd HH:mm:ss")
}

function convertMultiSeriesToCSV(
  csvRows: string[],
  chartData: ChartDataPoint[],
  splitKeys: string[],
  config: ChartWidgetConfig,
  displayNameLookups: DisplayNameLookups | null,
  timezone: string,
): string {
  const timeColumns = chartData.map((point) => formatCSVTime(point, timezone))
  const headerRow = ["Series", ...timeColumns.map(escapeCsvValue)]
  csvRows.push(headerRow.join(","))

  splitKeys.forEach((splitKey) => {
    const displayName = splitKeyToDisplayName(
      splitKey,
      config.groupBy || config.splitBy,
      displayNameLookups,
    )

    const row: string[] = [escapeCsvValue(displayName)]

    chartData.forEach((point) => {
      const value = point[splitKey]
      row.push(
        value === null || value === undefined ? "" : escapeCsvValue(value),
      )
    })

    csvRows.push(row.join(","))
  })

  return csvRows.join("\n")
}

function convertSingleSeriesToCSV(
  csvRows: string[],
  chartData: ChartDataPoint[],
  timezone: string,
): string {
  csvRows.push("Time,Value")

  chartData.forEach((point) => {
    const time = escapeCsvValue(formatCSVTime(point, timezone))
    const value =
      point.value === null || point.value === undefined
        ? ""
        : escapeCsvValue(point.value)

    csvRows.push(`${time},${value}`)
  })

  return csvRows.join("\n")
}

export function generateWidgetCSVFilename(
  widgetTitle: string,
  metricId: string,
  aggregation: string,
  dateRange?: { start?: Date; end?: Date },
): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, "0")
  const day = String(now.getDate()).padStart(2, "0")
  const hours = String(now.getHours()).padStart(2, "0")
  const minutes = String(now.getMinutes()).padStart(2, "0")
  const seconds = String(now.getSeconds()).padStart(2, "0")

  const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}`

  const sanitizedTitle = widgetTitle
    .replace(/[^a-zA-Z0-9]/g, "_")
    .substring(0, 30)

  const sanitizedMetric = metricId
    .replace(/[^a-zA-Z0-9]/g, "_")
    .substring(0, 30)

  let filename = `${sanitizedTitle}_${sanitizedMetric}_${aggregation}`

  if (dateRange?.start && dateRange?.end) {
    const startDate = toISODateString(dateRange.start)
    const endDate = toISODateString(dateRange.end)
    filename += `_${startDate}_to_${endDate}`
  }

  return `${filename}_${timestamp}.csv`
}

export function downloadWidgetCSV(csvData: string, filename: string): void {
  const csvWithBOM = `\uFEFF${csvData}`
  const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" })
  downloadBlob(blob, filename)
}

interface TableWidgetCSVExportOptions {
  widgetTitle: string
  tableData: {
    rows: TableRowData[]
    columns: {
      key: string
      label: string
      sortable: boolean
    }[]
  }
  dateRange?: {
    start?: Date
    end?: Date
  }
}

const dimensionDisplayNameKeys: Record<string, string> = {
  test_set_id: "testSetName",
  run_agent_id: "agentName",
  mutation_id: "mutationName",
  persona_id: "personaName",
  run_template_name: "templateName",
  assignee: "assignee",
}
const tableDimensionKeys = new Set([
  ...Object.keys(dimensionDisplayNameKeys),
  "metadataValue",
])

export function convertTableWidgetDataToCSV(
  options: TableWidgetCSVExportOptions,
): string {
  const { widgetTitle, tableData, dateRange } = options

  if (tableData.rows.length === 0 || tableData.columns.length === 0) {
    return ""
  }

  const csvRows: string[] = []
  const metricColumnKeys = tableData.columns
    .map((column) => column.key)
    .filter((key) => !tableDimensionKeys.has(key) && key !== "count")
  const showPerMetricDenominators = metricColumnKeys.length > 1

  csvRows.push(`# Widget: ${escapeCsvValue(widgetTitle)}`)

  if (dateRange?.start && dateRange?.end) {
    csvRows.push(
      `# Date Range: ${dateRange.start.toISOString()} to ${dateRange.end.toISOString()}`,
    )
  }

  csvRows.push(`# Timezone: ${escapeCsvValue(getUserTimezone())}`)

  csvRows.push("")

  const headerRow: string[] = tableData.columns.map((column) =>
    escapeCsvValue(column.label),
  )
  csvRows.push(headerRow.join(","))

  tableData.rows.forEach((row) => {
    const csvRow: string[] = []
    const rawRow = row as unknown as Record<string, string | number | undefined>

    tableData.columns.forEach((column) => {
      let value: string | number = ""
      const rawValue = rawRow[column.key]

      const displayNameKey = dimensionDisplayNameKeys[column.key]
      if (column.key === "metadataValue") {
        value = row.metadataValue
          ? getMetadataGroupDisplayName(row.metadataValue)
          : ""
      } else if (displayNameKey) {
        value = rawRow[displayNameKey] || rawValue || ""
      } else if (column.key === "count") {
        value = row.count ?? 0
      } else {
        value =
          formatMetricCellValue({
            cell: row.metricCells[column.key],
            showDenominator: showPerMetricDenominators,
          }) || ""
      }

      csvRow.push(`"${escapeCsvValue(value)}"`)
    })
    csvRows.push(csvRow.join(","))
  })

  return csvRows.join("\n")
}
